import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { SubscriptionModel } from '../Models/SubscriptionModel';
import type { UserModel } from '../Models/UserModel';
import type { PremiumFeature } from '../Models/enums';

/**
 * Who is signed in, and what they are entitled to.
 *
 * `subscription` is what the UI draws from, and it is only ever a hint: the
 * server refuses a premium call on its own, so a stale copy here shows the
 * wrong screen rather than handing anything out.
 *
 * `status` distinguishes "not signed in" from "not known yet". Without it the
 * app flashes the login screen on every reload while the token is checked.
 */
export type SessionStatus = 'unknown' | 'signed-out' | 'signed-in';

export interface SessionState {
  status: SessionStatus;
  user: UserModel | null;
  subscription: SubscriptionModel | null;
}

const initialState: SessionState = {
  status: 'unknown',
  user: null,
  subscription: null
};

const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    signedIn(state, action: PayloadAction<UserModel>) {
      state.status = 'signed-in';
      state.user = action.payload;
    },
    signedOut(state) {
      state.status = 'signed-out';
      state.user = null;
      state.subscription = null;
    },
    setSubscription(state, action: PayloadAction<SubscriptionModel | null>) {
      state.subscription = action.payload;
    }
  }
});

export const { signedIn, signedOut, setSubscription } = sessionSlice.actions;
export default sessionSlice.reducer;

/** The one place a component asks whether a premium screen may be shown. */
export function canUse(state: SessionState, feature: PremiumFeature): boolean {
  return state.subscription?.features.some(
    entitlement => entitlement.key === feature && entitlement.accessible
  ) ?? false;
}
